import WebUploader from "webuploader";
import "webuploader/dist/webuploader.css";
import { MessageBox } from 'element-ui';
import logServeCof from '@/plugins/logServeCof.js';
import Message from '@/utils/resetMessage';
import Evented from '@/utils/Evented.js';
import { getToken, getTokenId } from "@/utils/generalMethods";
import { Store } from "@/utils/store";

import {
    getConvertingQueue,
    getFileInfoService,
    createBimFolderUpload,
    createBimDataSetUpload,
    pauseBimFile,
    createInFolderCheckService,
    createInDataSetCheckService,
    createRsInFolderCheckService,
    createRsFoldertUpload,
    pauseRsFile,
    createOsgbFolderUpload,
    pauseOsgbFile,
    createOsgbInFolderCheckService,
    createPanoramaFolderUpload,
    pausePanoramaFile,
    checkPanoramaFileNameRepeats,
    createPointCloudFolderUpload,
    pausePointCloudFile,
    createPointCloudInFolderCheckService,
    createCadFolderUpload,
    pauseCadFile,
    createCadInFolderCheckService,
    createVectorInFolderCheckService,
    createVectorFolderUploadService,
    pauseVectorFileService,
} from "@/service/interface";

const BLOB_SIZE = 20 * 1024 * 1024;

const AppendInterfaceMap = {
    'Bim': 'modelFile',
    'Rs': 'demFile',
    'Wmts': 'wmtsFile',
    'Osgb': 'osgbFile',
    "Panorama": 'panoramaFile',
    "PointCloud": 'pointCloudFile',
    "Cad": "cadFile",
    "Vector": 'vectorFile'
}

const UploadingStatusValue = [0, 1, 2, 6];

WebUploader.Uploader.register({
    "before-send": "beforeSend", //每个分片上传前
},
    {
        beforeSend: (block) => {
            let file = block.file;
            let schemeType = file.schemeType;
            let uploaders = Store.uploaders;
            let find = uploaders.find((el) => el.schemeType === schemeType);
            let uploader = find.uploader;
            let uploadingFiles = uploader.uploadingFiles;

            let deferred = WebUploader.Deferred();
            let findIndex = uploadingFiles.findIndex((el) => el.fileName === file.fileName && el.length === file.length);
            if (findIndex === -1) {
                uploader.uploader.removeFile(file, true);
                // 猜想使用的方法--待验证
                deferred.reject();
            } else {
                let fileInfo = uploadingFiles[findIndex];

                let doneChunkPartIndex = fileInfo.blobIndex;
                if (doneChunkPartIndex > 0) {
                    if (block.chunk > doneChunkPartIndex) {
                        //分块不存在，重新发送该分块内容
                        deferred.resolve();
                    } else {
                        //分块存在，跳过
                        deferred.reject()
                    }
                } else {
                    //分块不存在，重新发送该分块内容
                    deferred.resolve();
                }
                return deferred.promise();
            }
        },
    }
);

class CustomUploader extends Evented {
    constructor(schemeType, option) {
        super();
        this.schemeType = schemeType;
        this.option = option;

        // status为前端自定义状态，状态说明如下：
        // waiting：某个文件等待上传
        // continue：某个文件断点续传
        // update：某个文件重新上传
        this.status = "waiting";

        this.uploadFiles = [];          // 等待上传的文件
        this.uploadingFiles = [];       // 上传中的文件
        this.pausedFiles = [];          // 上传暂停的文件
        this.exceptionFiles = [];       // 上传异常文件

        this.currProjId = "";
        this.currDataSetId = "";
        this.currNodeType = 1;
        this.uploadPathName = "";
        this.continueFlag = false;
        this.currFileInfo = null;       // 设置文件信息，用于断点续传和文件更新

        this.onFilesQueued = this.onFilesQueued.bind(this);
        this.onUploadBeforeSend = this.onUploadBeforeSend.bind(this);
        this.onUploadAccept = this.onUploadAccept.bind(this);
        this.onUploadSuccess = this.onUploadSuccess.bind(this);
        this.onUploadError = this.onUploadError.bind(this);

        this.onHandleFileException = this.onHandleFileException.bind(this);
        this.onPauseFileUpload = this.onPauseFileUpload.bind(this);
        this.onContinueFileUpload = this.onContinueFileUpload.bind(this);
        this.downloadFiles = this.downloadFiles.bind(this);

        this.initUploader();

        this.addUploaderEventsListener();
        this.addEvnetListener();
    }

    initUploader() {
        const { uploaders } = Store;
        const find = uploaders.find(el => el.schemeType === this.schemeType);
        if (!find) {
            this.uploader = this.createUploader();
            uploaders.push({ schemeType: this.schemeType, uploader: this });
        }
    }

    createUploader() {
        let webUploader = WebUploader.create({
            // swf文件路径
            swf: "../../node_modules/webuploader/dist/Uploader.swf",

            // 文件接收服务端
            server: `${window.serverUrl}/${logServeCof.logServeCof()}/project/${AppendInterfaceMap[this.schemeType]}/v3/append`,
            timeout: window.uploadTimeout,

            accept: this.option.accept,
            chunked: true, // 分片处理大文件
            chunkSize: BLOB_SIZE,
            fileVal: "FileInfo",
            threads: 1,
            chunkRetry: 0,
            duplicate: true,

            auto: false,
        });
        return webUploader;
    }

    addUploaderEventsListener() {
        this.uploader.on("filesQueued", this.onFilesQueued);
        this.uploader.on("uploadBeforeSend", this.onUploadBeforeSend);
        this.uploader.on("uploadAccept", this.onUploadAccept);
        this.uploader.on("uploadSuccess", this.onUploadSuccess);
        this.uploader.on("uploadError", this.onUploadError);
    }

    addEvnetListener() {
        this.on('handleFileException', this.onHandleFileException);
        this.on('pauseFileUpload', this.onPauseFileUpload);
        this.on('continueFileUpload', this.onContinueFileUpload);
        this.on('fileDownload', this.downloadFiles);
    }

    onFilesQueued(files) {
        let filesInUploadQueue = [];
        let invalidFileNamefiles = [];      // 文件名不合法的文件   
        let vectorInvalidFiles = [];
        files.forEach(file => {
            file.fileName = file.name;
            file.length = file.size;
            file.schemeType = this.schemeType;

            let fileExtensName = file.name.substring(file.name.lastIndexOf(".") + 1);
            let fileInfo = {
                id: WebUploader.Base.guid(),
                projId: this.currProjId,
                nodeType: this.currNodeType,
                dataSetId: this.currDataSetId,
                uploadPathName: this.uploadPathName,
                schemeType: this.schemeType,
                // 文件状态，前端自定义(waiting: 等待上传, created: 已创建上传索引, paused: 已暂停, continue: 断点续传, update: 文件更新)
                customStatus: this.status,

                // 是否已经过重名校验
                isChecked: this.isChecked(),

                // 用于断点续传
                fileDataId: file.fileDataId ? file.fileDataId : undefined,

                // 以下为上传列表中需要展示的内容
                fileName: file.name,
                fileType: fileExtensName,
                length: file.size,
                creationTime: new Date().toUTCString(),
                rate: 0,
                status: this.getFileStatus(),

                // 文件对象
                file,
            }

            if (this.status === 'continue') {
                if (this.currFileInfo.fileName !== fileInfo.fileName) {
                    Message.warning("文件名不匹配，请重试");
                    return;
                }
                fileInfo["fileId"] = this.currFileInfo.fileId;
                fileInfo["dataSetId"] = this.currFileInfo.dataSetId;
                fileInfo["fileDataId"] = this.currFileInfo.fileDataId;
            }

            if (this.status === 'update') {
                let isCheckPass = this.isUpdateCheckPass(this.currFileInfo, fileInfo);
                fileInfo["dataSetId"] = this.currFileInfo.dataSetId;
                if (!isCheckPass) return
            }

            let isFileInUploadQueue = this.isFileInUploadQueue(fileInfo);
            let isInvalidFileName = this.isInvalidFileName(fileInfo);
            let isVectorInvalidFileName = this.isVectorInvalidFileName(fileInfo);
            if (isFileInUploadQueue) {
                filesInUploadQueue.push(fileInfo);
            } else if (isInvalidFileName) {
                invalidFileNamefiles.push(fileInfo);
            } else if (isVectorInvalidFileName) {
                vectorInvalidFiles.push(fileInfo);
            } else {
                this.uploadFiles.push(fileInfo);
            }
        })

        if (filesInUploadQueue.length) {
            let fileNameList = filesInUploadQueue.map(el => el.fileName);
            let message = this.getWarningMessage(fileNameList, '已在上传队列')
            Message.error(message);
        }

        if (invalidFileNamefiles.length) {
            let fileNameList = invalidFileNamefiles.map(el => el.fileName);
            let message = this.getWarningMessage(fileNameList, '名称存在无效的字符(开头与结尾不能为空，不能包含\\字段，文件名称后缀前不允许有空格)')
            Message.error(message);
        }

        if (vectorInvalidFiles.length) {
            let message = 'shp文件仅支持后缀名为.shp.zip格式';
            Message.error(message);
        }

        this.handleUploadFiles();
    }

    onUploadBeforeSend(object, data, headers) {
        headers["Authorization"] = getToken();

        data["Index"] = data.chunk ? data.chunk : 0;

        if (this.continueFlag) {
            this.setAppendUrl('append');
            this.continueFlag = false;
        }
    }

    onUploadAccept(object, response) {
        // 上传过程中接口报错，暂停该文件的上传
        if (!response.isSuccess) {
            let fileInfo = this.uploadingFiles.find((el) => this.isSameFile(el, object.file));
            let pauseInfo = {
                fileId: fileInfo.fileId,
                fileDataId: fileInfo.fileDataId,
                key: fileInfo.Key,
                fileName: fileInfo.fileName,
                length: fileInfo.length,
                dataSetId: fileInfo.dataSetId
            }
            this.uploader.stop(true);
            this.pauseFileUpload(pauseInfo);
        } else {
            let data = this.uploader.options.formData;
            if (data.pauseFlag) {
                // webuploader的stop方法有两种模式
                // 1、暂停自动上传下个文件，但是当前文件会继续传完。
                // 2、暂停自动上传下个文件，且中断当前文件上传。(如果是分块上传，只会中断当前分块)
                // 此处需要暂停当前文件的继续上传，因此需要传入true
                this.uploader.stop(true);
                this.pauseFileUpload(data.pauseInfo);
            }
        }

    }

    onUploadSuccess(file) {
        let findIndex = this.uploadingFiles.findIndex((el) => this.isSameFile(el, file));
        if (findIndex !== -1) {
            this.uploadingFiles.splice(findIndex, 1);
            this.uploader.removeFile(file, true);
            this.uploader.stop();
            this.status === "waiting";

            if (this.uploadFiles.length) {
                let uploadingFile = this.uploadFiles.shift();
                this.uploadingFiles.push(uploadingFile);
                this.handleUploadSuccess(uploadingFile);
            }
        }
    }

    onUploadError(file) {
        let findIndex = this.uploadingFiles.findIndex((el) => this.isSameFile(el, file));
        if (findIndex !== -1) {
            this.uploadingFiles.splice(findIndex, 1);
            this.uploader.removeFile(file, true);
            this.uploader.stop();
            this.status === "waiting";

            if (this.uploadFiles.length) {
                let uploadingFile = this.uploadFiles.shift();
                this.uploadingFiles.push(uploadingFile);
                this.handleUploadSuccess(uploadingFile);
            }
        }
    }

    handleUploadFiles() {
        let filesToCheck = this.uploadFiles.filter(el => !el.isChecked);
        this.uploadFileCheck(filesToCheck).then(result => {
            let exceptionFiles = result.exceptionFiles;
            if (exceptionFiles.length) {
                this.exceptionFiles = exceptionFiles;
                let isSingleException = this.isSingleException(exceptionFiles);
                if (isSingleException) {
                    this.singleExceptionClassify();
                } else {
                    this.mutiExceptionClassify();
                }
            } else {
                if (this.uploadFiles.length) {
                    this.startUploadFile();
                }
            }
        })
    }

    singleExceptionClassify() {
        let obj = {};
        let extNameExceptionFind = this.exceptionFiles.find(el => el.isExtException);
        if (extNameExceptionFind) {
            obj = {
                eventSource: "uploader",
                errType: 'extNameException',
                errMsg: extNameExceptionFind.errMsg,
                files: extNameExceptionFind.files,
                count: extNameExceptionFind.count,
            }
            this.fire('fileException', { eventInfo: obj });
            return;
        }

        let sceneQuoteExceptionFind = this.exceptionFiles.find(el => el.isSceneQuoted);
        if (sceneQuoteExceptionFind) {
            let dataSetName = sceneQuoteExceptionFind.quoteData[0].dataSetName;
            let errMsg = '';
            if (dataSetName) {
                if (sceneQuoteExceptionFind.isUpdateException) {
                    errMsg = `所选文件与当前文件名不一致，且原文件已被场景引用，更新后可能会导致场景发生变化，是否更新`
                } else {
                    errMsg = `${dataSetName}已存在，并被场景引用，覆盖后可能会导致场景发生变化，是否覆盖`
                }
            } else {
                if (sceneQuoteExceptionFind.isUpdateException) {
                    errMsg = `所选文件与当前文件名不一致，${sceneQuoteExceptionFind.errMsg}`;
                } else {
                    errMsg = sceneQuoteExceptionFind.errMsg;
                }
            }

            obj = {
                eventSource: "uploader",
                errType: 'sceneQuoteException',
                errMsg,
                quoteData: sceneQuoteExceptionFind.quoteData,
                count: sceneQuoteExceptionFind.count,
            }
        }

        let existExceptionFind = this.exceptionFiles.find(el => el.isOverWrite);
        if (existExceptionFind) {
            let fileName = existExceptionFind.files[0].fileName;
            let errMsg = ""
            if (existExceptionFind.isUpdateException) {
                errMsg = `所选文件与当前文件名不一致，是否更新`
            } else {
                errMsg = `${fileName}已存在，是否覆盖`
            }
            obj = {
                eventSource: "uploader",
                errType: 'existException',
                errMsg,
                existFiles: existExceptionFind.files,
                count: existExceptionFind.count,
            }
        }

        let isConvertingFind = this.exceptionFiles.find(el => el.isConverting);
        if (isConvertingFind) {
            let fileName = isConvertingFind.files[0].fileName;
            let errMsg = ""
            if (existExceptionFind.isUpdateException) {
                errMsg = `所选文件与当前文件名不一致，且原文件正在转换，请稍后再试`
            } else {
                errMsg = `${fileName}正在转换，请稍后再试`
            }
            obj = {
                eventSource: "uploader",
                errType: 'convertingException',
                errMsg,
                convertingFiles: isConvertingFind.files,
                count: isConvertingFind.count,
            }
        }

        let updateExceptionFind = this.exceptionFiles.find(el => el.isUpdateException);
        if (!Object.keys(obj).length && updateExceptionFind) {
            let errMsg = "";
            if (updateExceptionFind.isUpdateFileExistException) {
                errMsg = "所选文件已存在，请选择其他文件"
                obj = {
                    eventSource: "uploader",
                    errType: 'updateFileExistException',
                    errMsg,
                    convertingFiles: updateExceptionFind.files,
                    count: updateExceptionFind.count,
                }
            } else {
                errMsg = "所选文件与当前文件名不一致，是否更新";
                obj = {
                    eventSource: "uploader",
                    errType: 'updateFileException',
                    errMsg,
                    convertingFiles: updateExceptionFind.files,
                    count: updateExceptionFind.count,
                }
            }
        }

        this.fire('fileException', { eventInfo: obj });
    }

    mutiExceptionClassify() {
        let obj = {};
        let extNameExceptionFind = this.exceptionFiles.find(el => el.isExtException);
        if (extNameExceptionFind) {
            obj = {
                eventSource: "uploader",
                errType: 'extNameException',
                errMsg: extNameExceptionFind.errMsg,
                files: extNameExceptionFind.files,
                count: extNameExceptionFind.count,
            }
            this.fire('fileException', { eventInfo: obj });
            return;
        }

        let existExceptionFind = this.exceptionFiles.find(el => el.isOverWrite);
        let sceneQuoteExceptionFind = this.exceptionFiles.find(el => el.isSceneQuoted);
        if (existExceptionFind && sceneQuoteExceptionFind) {
            let errMsg = `${existExceptionFind.count}个文件已存在，${sceneQuoteExceptionFind.count}个文件被场景引用，覆盖后可能会导致场景发生变化，是否覆盖`
            obj = {
                eventSource: "uploader",
                errType: 'existAndQuoteException',
                errMsg,
                existFiles: existExceptionFind.files,
                quoteData: sceneQuoteExceptionFind.quoteData,
                count: existExceptionFind.count + sceneQuoteExceptionFind.count,
            }
            this.fire('fileException', { eventInfo: obj });
            return;
        }


        if (existExceptionFind) {
            let errMsg = `${existExceptionFind.count}个文件已存在，是否覆盖`
            obj = {
                eventSource: "uploader",
                errType: 'existException',
                errMsg,
                existFiles: existExceptionFind.files,
                count: existExceptionFind.count,
            }
        }


        if (sceneQuoteExceptionFind) {
            let errMsg = `${sceneQuoteExceptionFind.count}个文件已存在，并被场景引用，覆盖后可能会导致场景发生变化，是否覆盖`
            obj = {
                eventSource: "uploader",
                errType: 'sceneQuoteException',
                errMsg,
                quoteData: sceneQuoteExceptionFind.quoteData,
                count: sceneQuoteExceptionFind.count,
            }
        }

        this.fire('fileException', { eventInfo: obj });
    }

    onHandleFileException(events) {
        let eventInfo = events.eventInfo;
        let order = eventInfo.order;
        if (order === 'cancel') {
            this.terminateUpload();
        } else {
            this.handleException().then(() => {
                if (this.uploadFiles.length) {
                    this.startUploadFile();
                }
            });
        }
    }

    handleException() {
        return new Promise(resolve => {
            let exceptionFiles = this.exceptionFiles.filter(el => el.isOverWrite);
            if (exceptionFiles.length) {
                let repeatedFiles = exceptionFiles[0].files;
                this.removeUploadingFilesFromUploadFiles(repeatedFiles);

                let convertingExceptionFind = exceptionFiles.find(el => el.isConverting);
                if (convertingExceptionFind) {
                    let convertingFiles = convertingExceptionFind[0].files;
                    if (convertingFiles.length) {
                        let fileNameList = convertingFiles.map(el => el.fileName);
                        let message = this.getWarningMessage(fileNameList, '正在转换或等待转换，暂时不能上传')
                        Message.error(message);

                        this.removeConvertingFilesFromUploadFiles(convertingFiles);
                    }
                }
            }
            resolve();
        })
    }

    isSingleException(exceptionFiles) {
        let count = 0;
        exceptionFiles.forEach(item => {
            count += item.count;
        })
        if (count === 1) {
            return true;
        } else {
            return false;
        }
    }

    startUploadFile() {
        this.fire('startUploadFile', {});

        if (this.uploadingFiles.length) {
            Message.warning("当前有文件正在上传，请耐心等待");
            return;
        }

        let uploadingFile = this.uploadFiles.shift();

        if (uploadingFile.customStatus === 'waiting') {
            this.uploadingFiles.push(uploadingFile);

            this.createFileUpLoad(uploadingFile).then(() => {
                this.uploader.upload();
            });
        }

        if (uploadingFile.customStatus === 'continue') {
            this.uploadingFiles.push(uploadingFile);

            this.getFileInfo(uploadingFile).then(() => {
                this.continueFlag = true;
                this.setAppendUrl('reAppend');

                this.uploader.upload();
            });
        }

        if (uploadingFile.customStatus === 'update') {
            this.uploadingFiles.push(uploadingFile);

            this.createFileUpLoad(uploadingFile).then(() => {
                this.uploader.upload();
            });
        }
    }

    handleUploadSuccess(uploadingFile) {
        if (uploadingFile.customStatus === 'waiting') {
            this.createFileUpLoad(uploadingFile).then(() => {
                // 延时执行以触发文件上传事件
                setTimeout(() => {
                    this.uploader.upload();
                }, 500);

            });
        }
        if (uploadingFile.customStatus === 'continue') {
            this.getFileInfo(uploadingFile).then(() => {
                // 延时执行以触发文件上传事件
                setTimeout(() => {
                    this.continueFlag = true;
                    this.setAppendUrl('reAppend');

                    this.uploader.upload();
                }, 500);
            });
        }
        if (uploadingFile.customStatus === 'update') {
            this.createFileUpLoad(uploadingFile).then(() => {
                // 延时执行以触发文件上传事件
                setTimeout(() => {
                    this.uploader.upload();
                }, 500);
            });
        }
    }

    createFileUpLoad(fileInfo) {
        return new Promise((resolve) => {

            let count = fileInfo.length / (BLOB_SIZE);
            let params = {
                fileName: fileInfo.fileName,
                fileSize: fileInfo.length,
                blobCount: Math.ceil(count),
                blobSize: BLOB_SIZE,
                projId: fileInfo.projId,
                isOverWrite: true
            };

            switch (this.schemeType) {
                case 'Bim': {
                    if (fileInfo.nodeType === 2) {
                        params.dataSetId = fileInfo.dataSetId;
                        createBimDataSetUpload(params).then((response) => {
                            if (response.isSuccess) {
                                let result = response.data;
                                if (!result.fileUploaderOverflow) {
                                    this.uploader.options.formData["FileId"] = result.fileId;
                                    this.uploader.options.formData["FileDataId"] = result.fileDataId;
                                    this.uploader.options.formData["Key"] = result.fileKey;

                                    fileInfo['dataSetId'] = result.dataSetId;
                                    fileInfo["fileId"] = result.fileId;
                                    fileInfo["fileDataId"] = result.fileDataId;
                                    fileInfo["Key"] = result.fileKey;

                                    resolve(result);
                                } else {
                                    this.handleFileOverflow(fileInfo);
                                }
                            } else {
                                Message.error(response.errMsg);
                                this.handleFileCreateFail(fileInfo);
                            }
                        });
                    } else {
                        params.nodeId = fileInfo.dataSetId;
                        createBimFolderUpload(params).then((response) => {
                            if (response.isSuccess) {
                                let result = response.data;
                                if (!result.fileUploaderOverflow) {
                                    this.uploader.options.formData["FileId"] = result.fileId;
                                    this.uploader.options.formData["FileDataId"] = result.fileDataId;
                                    this.uploader.options.formData["Key"] = result.fileKey;

                                    fileInfo['dataSetId'] = result.dataSetId;
                                    fileInfo["fileId"] = result.fileId;
                                    fileInfo["fileDataId"] = result.fileDataId;
                                    fileInfo["Key"] = result.fileKey;

                                    resolve(result);
                                } else {
                                    this.handleFileOverflow(fileInfo);
                                }
                            } else {
                                Message.error(response.errMsg);
                                this.handleFileCreateFail(fileInfo);
                            }
                        });
                    }
                    break;
                }
                case 'Rs': {
                    params.nodeId = fileInfo.dataSetId;
                    createRsFoldertUpload(params).then((response) => {
                        if (response.isSuccess) {
                            let result = response.data;
                            if (!result.fileUploaderOverflow) {
                                this.uploader.options.formData["FileId"] = result.fileId;
                                this.uploader.options.formData["FileDataId"] = result.fileDataId;
                                this.uploader.options.formData["Key"] = result.fileKey;

                                fileInfo['dataSetId'] = result.dataSetId;
                                fileInfo["fileId"] = result.fileId;
                                fileInfo["fileDataId"] = result.fileDataId;
                                fileInfo["Key"] = result.fileKey;

                                resolve(result);
                            } else {
                                this.handleFileOverflow(fileInfo);
                            }
                        } else {
                            Message.error(response.errMsg);
                            this.handleFileCreateFail(fileInfo);
                        }
                    });
                    break;
                }
                case 'Osgb': {
                    params.nodeId = fileInfo.dataSetId;
                    createOsgbFolderUpload(params).then((response) => {
                        if (response.isSuccess) {
                            let result = response.data;
                            if (!result.fileUploaderOverflow) {
                                this.uploader.options.formData["FileId"] = result.fileId;
                                this.uploader.options.formData["FileDataId"] = result.fileDataId;
                                this.uploader.options.formData["Key"] = result.fileKey;

                                fileInfo['dataSetId'] = result.dataSetId;
                                fileInfo["fileId"] = result.fileId;
                                fileInfo["fileDataId"] = result.fileDataId;
                                fileInfo["Key"] = result.fileKey;

                                resolve(result);
                            } else {
                                this.handleFileOverflow(fileInfo);
                            }
                        } else {
                            Message.error(response.errMsg);
                            this.handleFileCreateFail(fileInfo);
                        }
                    });
                    break;
                }
                case 'Panorama': {
                    params.nodeId = fileInfo.dataSetId;
                    createPanoramaFolderUpload(params).then((response) => {
                        if (response.isSuccess) {
                            let result = response.data;
                            if (!result.fileUploaderOverflow) {
                                this.uploader.options.formData["FileId"] = result.fileId;
                                this.uploader.options.formData["FileDataId"] = result.fileDataId;
                                this.uploader.options.formData["Key"] = result.fileKey;

                                fileInfo['dataSetId'] = result.dataSetId;
                                fileInfo["fileId"] = result.fileId;
                                fileInfo["fileDataId"] = result.fileDataId;
                                fileInfo["Key"] = result.fileKey;

                                resolve(result);
                            } else {
                                this.handleFileOverflow(fileInfo);
                            }
                        } else {
                            Message.error(response.errMsg);
                            this.handleFileCreateFail(fileInfo);
                        }
                    });
                    break;
                }
                case 'PointCloud': {
                    params.nodeId = fileInfo.dataSetId;
                    createPointCloudFolderUpload(params).then((response) => {
                        if (response.isSuccess) {
                            let result = response.data;
                            if (!result.fileUploaderOverflow) {
                                this.uploader.options.formData["FileId"] = result.fileId;
                                this.uploader.options.formData["FileDataId"] = result.fileDataId;
                                this.uploader.options.formData["Key"] = result.fileKey;

                                fileInfo['dataSetId'] = result.dataSetId;
                                fileInfo["fileId"] = result.fileId;
                                fileInfo["fileDataId"] = result.fileDataId;
                                fileInfo["Key"] = result.fileKey;

                                resolve(result);
                            } else {
                                this.handleFileOverflow(fileInfo);
                            }
                        } else {
                            Message.error(response.errMsg);
                            this.handleFileCreateFail(fileInfo);
                        }
                    });
                    break;
                }
                case 'Cad': {
                    params.nodeId = fileInfo.dataSetId;
                    createCadFolderUpload(params).then((response) => {
                        if (response.isSuccess) {
                            let result = response.data;
                            if (!result.fileUploaderOverflow) {
                                this.uploader.options.formData["FileId"] = result.fileId;
                                this.uploader.options.formData["FileDataId"] = result.fileDataId;
                                this.uploader.options.formData["Key"] = result.fileKey;

                                fileInfo['dataSetId'] = result.dataSetId;
                                fileInfo["fileId"] = result.fileId;
                                fileInfo["fileDataId"] = result.fileDataId;
                                fileInfo["Key"] = result.fileKey;

                                resolve(result);
                            } else {
                                this.handleFileOverflow(fileInfo);
                            }
                        } else {
                            Message.error(response.errMsg);
                            this.handleFileCreateFail(fileInfo);
                        }
                    });
                    break;
                }
                case 'Vector': {
                    params.nodeId = fileInfo.dataSetId;
                    createVectorFolderUploadService(params).then((response) => {
                        if (response.isSuccess) {
                            let result = response.data;
                            if (!result.fileUploaderOverflow) {
                                this.uploader.options.formData["FileId"] = result.fileId;
                                this.uploader.options.formData["FileDataId"] = result.fileDataId;
                                this.uploader.options.formData["Key"] = result.fileKey;

                                fileInfo['dataSetId'] = result.dataSetId;
                                fileInfo["fileId"] = result.fileId;
                                fileInfo["fileDataId"] = result.fileDataId;
                                fileInfo["Key"] = result.fileKey;

                                resolve(result);
                            } else {
                                this.handleFileOverflow(fileInfo);
                            }
                        } else {
                            Message.error(response.errMsg);
                            this.handleFileCreateFail(fileInfo);
                        }
                    });
                    break;
                }
            }
        });
    }

    onPauseFileUpload(eventInfo) {
        let { fileInfo } = eventInfo;

        let find = this.uploadingFiles.find(el => el.fileDataId === fileInfo.fileDataId);
        if (find) {
            this.uploader.options.formData.pauseFlag = true;
            this.uploader.options.formData.pauseInfo = {
                fileId: fileInfo.fileId,
                fileDataId: fileInfo.fileDataId,
                key: fileInfo.key,
                fileName: fileInfo.fileName,
                length: fileInfo.length,
                dataSetId: fileInfo.dataSetId
            }
        } else {
            this.pauseFileUpload(fileInfo);
        }
    }

    pauseFileUpload(pauseInfo) {
        let params = {
            fileId: pauseInfo.fileId,
            fileDataId: pauseInfo.fileDataId,
            fileKey: pauseInfo.key
        }

        switch (this.schemeType) {
            case 'Bim': {
                pauseBimFile(params).then((response) => {
                    if (response.isSuccess) {
                        this.handleFilePauseSuccess(pauseInfo);
                    }
                });
                break;
            }
            case 'Rs': {
                pauseRsFile(params).then((response) => {
                    if (response.isSuccess) {
                        this.handleFilePauseSuccess(pauseInfo);
                    }
                });
                break;
            }
            case 'Osgb': {
                pauseOsgbFile(params).then((response) => {
                    if (response.isSuccess) {
                        this.handleFilePauseSuccess(pauseInfo);
                    }
                });
                break;
            }
            case 'Panorama': {
                pausePanoramaFile(params).then((response) => {
                    if (response.isSuccess) {
                        this.handleFilePauseSuccess(pauseInfo);
                    }
                });
                break;
            }
            case 'PointCloud': {
                pausePointCloudFile(params).then((response) => {
                    if (response.isSuccess) {
                        this.handleFilePauseSuccess(pauseInfo);
                    }
                });
                break;
            }
            case 'Cad': {
                pauseCadFile(params).then((response) => {
                    if (response.isSuccess) {
                        this.handleFilePauseSuccess(pauseInfo);
                    }
                });
                break;
            }
            case 'Vector': {
                pauseVectorFileService(params).then((response) => {
                    if (response.isSuccess) {
                        this.handleFilePauseSuccess(pauseInfo);
                    }
                });
                break;
            }
        }
    }

    onContinueFileUpload(eventInfo) {
        let { fileInfo } = eventInfo;
        let findIndex = this.pausedFiles.findIndex((el) => el.fileDataId === fileInfo.fileDataId);
        if (findIndex !== -1) {
            let uploadingFile = this.pausedFiles[findIndex];
            this.pausedFiles.splice(findIndex, 1);

            this.continueFlag = true;
            this.setAppendUrl('reAppend');

            this.uploader.addFiles(uploadingFile.file);
        }
    }

    uploadFileCheck(filesToCheck) {
        return new Promise(resolve => {
            if (!filesToCheck.length) {
                resolve({ exceptionFiles: [] });
                return;
            }
            filesToCheck.forEach(el => el.isChecked = true);

            switch (this.schemeType) {
                case 'Bim': {
                    this.bimUploadFileCheck(filesToCheck).then(exceptionFiles => {
                        resolve(exceptionFiles)
                    })
                    break;
                }
                case 'Rs': {
                    this.rsUploadFileCheck(filesToCheck).then(exceptionFiles => {
                        resolve(exceptionFiles)
                    })
                    break;
                }
                case 'Osgb': {
                    this.osgbUploadFileCheck(filesToCheck).then(exceptionFiles => {
                        resolve(exceptionFiles)
                    })
                    break;
                }
                case 'Panorama': {
                    this.panoramaUploadFileCheck(filesToCheck).then(exceptionFiles => {
                        resolve(exceptionFiles)
                    })
                    break;
                }
                case 'PointCloud': {
                    this.pointClounUploadFileCheck(filesToCheck).then(exceptionFiles => {
                        resolve(exceptionFiles)
                    })
                    break;
                }
                case 'Cad': {
                    this.cadUploadFileCheck(filesToCheck).then(exceptionFiles => {
                        resolve(exceptionFiles)
                    })
                    break;
                }
                case 'Vector': {
                    this.vectorUploadFileCheck(filesToCheck).then(exceptionFiles => {
                        resolve(exceptionFiles)
                    })
                    break;
                }
            }
        })
    }

    isUpdateCheckPass(currFileInfo, fileInfo) {
        let isCheckPass = false;
        let { formatType, fileName } = currFileInfo
        if (this.schemeType === 'Bim' && formatType === 1) {
            isCheckPass = true;
            if (fileName !== fileInfo.fileName) {
                fileInfo.isUpdateException = true;
            }
        } else {
            if (fileName !== fileInfo.fileName) {
                Message.warning("文件名不一致，请重试");
            } else {
                isCheckPass = true;
            }
        }

        return isCheckPass
    }

    bimUploadFileCheck(filesToCheck) {
        return new Promise(resolve => {
            let exceptionFiles = [];
            if (this.currNodeType === 2) {
                createInDataSetCheckService({ dataSetId: this.currDataSetId }).then(response => {
                    if (response.isSuccess) {
                        let data = response.data;
                        if (data.refScenes.length) {
                            let quoteData = data.refScenes;
                            let obj = {
                                errMsg: data.message,
                                isModelGroup: true,
                                isExtException: false,
                                isSceneQuoted: true,
                                isOverWrite: false,
                                isConverting: false,
                                quoteData,
                                count: 1,
                            };
                            exceptionFiles.push(obj);
                        }
                        resolve({ exceptionFiles });
                    }
                })
            } else {
                let params = {
                    projId: this.currProjId,
                    parentId: this.currDataSetId,
                    isUpdate: this.status === 'update' ? true : false,
                    fileNames: filesToCheck.map(el => el.fileName)
                }
                if (this.status === 'update') {
                    params.dataSetId = this.currFileInfo.dataSetId
                }
                createInFolderCheckService(params).then(response => {
                    if (response.isSuccess) {
                        let data = response.data;
                        let isUpdateExceptionExist = filesToCheck.some(el => el.isUpdateException);
                        if (!data.checkResult) {
                            let obj = {};
                            if (data.errorFileNames && data.errorFileNames.length) {
                                let files = [];
                                filesToCheck.forEach(item => {
                                    let find = data.errorFileNames.find(el => item.fileName === el);
                                    if (find) {
                                        files.push({ fileName: item.fileName, length: item.length });
                                    }
                                })
                                obj = {
                                    errMsg: data.message,
                                    isModelGroup: false,
                                    isExtException: true,
                                    isUpdateException: isUpdateExceptionExist,
                                    isUpdateFileExistException: false,
                                    isSceneQuoted: false,
                                    isOverWrite: false,
                                    isConverting: false,
                                    files,
                                    count: files.length,
                                };
                            } else {
                                let files = [];
                                filesToCheck.forEach(item => {
                                    if (item.isUpdateException) {
                                        files.push({ fileName: item.fileName, length: item.length });
                                    }
                                })
                                obj = {
                                    errMsg: data.message,
                                    isModelGroup: false,
                                    isExtException: false,
                                    isUpdateException: isUpdateExceptionExist,
                                    isUpdateFileExistException: true,
                                    isSceneQuoted: false,
                                    isOverWrite: false,
                                    isConverting: false,
                                    files,
                                    count: files.length,
                                };
                            }

                            exceptionFiles.push(obj);
                        }
                        if (data.dataSetIds.length) {
                            let quoteData = data.dataSetIds;
                            let obj = {
                                errMsg: '文件被场景引用',
                                isModelGroup: false,
                                isExtException: false,
                                isUpdateException: isUpdateExceptionExist,
                                isUpdateFileExistException: false,
                                isSceneQuoted: true,
                                isOverWrite: false,
                                isConverting: false,
                                quoteData,
                                count: quoteData.length,
                            };
                            exceptionFiles.push(obj);
                        }

                        let repeatedFiles = [...data.repeateds, ...data.specialStatus];
                        getConvertingQueue({}).then(res => {
                            if (res.isSuccess) {
                                let result = res.data;
                                let convertingFiles = [];
                                if (result.length) {
                                    let projQueuing = result;
                                    let nodeIds = projQueuing.map(el => el.dataSetId);
                                    convertingFiles = repeatedFiles.filter(el => nodeIds.includes(el.dataSetId));
                                    repeatedFiles = repeatedFiles.filter(el => !nodeIds.includes(el.dataSetId));
                                }

                                let repeatFiles = [];
                                let isConvertingFiles = [];
                                filesToCheck.forEach(item => {
                                    let repeatFind = repeatedFiles.find(el => item.fileName === el.fileName);
                                    if (repeatFind) {
                                        repeatFiles.push({ fileName: item.fileName, length: item.length, status: repeatFind.status.value });
                                    }

                                    let convertingFind = convertingFiles.find(el => item.fileName === el.fileName);
                                    if (convertingFind) {
                                        isConvertingFiles.push({ fileName: item.fileName, length: item.length, status: convertingFind.status.value });
                                    }
                                })
                                let repeatObj = {
                                    errMsg: '文件已存在',
                                    isModelGroup: false,
                                    isExtException: false,
                                    isUpdateException: isUpdateExceptionExist,
                                    isUpdateFileExistException: false,
                                    isSceneQuoted: false,
                                    isOverWrite: true,
                                    isConverting: false,
                                    files: repeatFiles,
                                    count: repeatFiles.length,
                                };
                                if (repeatFiles.length) {
                                    exceptionFiles.push(repeatObj);
                                }

                                let convertingObj = {
                                    errMsg: '文件正在转换',
                                    isModelGroup: false,
                                    isExtException: false,
                                    isUpdateException: isUpdateExceptionExist,
                                    isUpdateFileExistException: false,
                                    isSceneQuoted: false,
                                    isOverWrite: false,
                                    isConverting: true,
                                    files: isConvertingFiles,
                                    count: isConvertingFiles.length,
                                };
                                if (isConvertingFiles.length) {
                                    exceptionFiles.push(convertingObj);
                                }

                                if (!exceptionFiles.length && isUpdateExceptionExist) {
                                    let files = [];
                                    filesToCheck.forEach(item => {
                                        if (item.isUpdateException) {
                                            files.push({ fileName: item.fileName, length: item.length });
                                        }
                                    })
                                    let obj = {
                                        errMsg: data.message,
                                        isModelGroup: false,
                                        isExtException: false,
                                        isUpdateException: isUpdateExceptionExist,
                                        isUpdateFileExistException: false,
                                        isSceneQuoted: false,
                                        isOverWrite: false,
                                        isConverting: false,
                                        files,
                                        count: files.length,
                                    };
                                    exceptionFiles.push(obj);
                                }
                                resolve({ exceptionFiles });
                            }
                        })
                    }
                })
            }
        })
    }

    rsUploadFileCheck(filesToCheck) {
        return new Promise(resolve => {
            let exceptionFiles = [];
            let params = {
                projId: this.currProjId,
                parentId: this.currDataSetId,
                isUpdate: this.status === 'update' ? true : false,
                fileNames: filesToCheck.map(el => el.fileName)
            }
            createRsInFolderCheckService(params).then(response => {
                if (response.isSuccess) {
                    let data = response.data;
                    if (!data.checkResult) {
                        let files = [];
                        filesToCheck.forEach(item => {
                            let find = data.errorFileNames.find(el => item.fileName === el);
                            if (find) {
                                files.push({ fileName: item.fileName, length: item.length });
                            }
                        })
                        let obj = {
                            errMsg: data.message,
                            isModelGroup: false,
                            isExtException: true,
                            isSceneQuoted: false,
                            isOverWrite: false,
                            isConverting: false,
                            files,
                            count: files.length,
                        };
                        exceptionFiles.push(obj);
                    }
                    if (data.dataSetIds.length) {
                        let quoteData = data.dataSetIds;
                        let obj = {
                            errMsg: '文件被场景引用',
                            isModelGroup: false,
                            isExtException: false,
                            isSceneQuoted: true,
                            isOverWrite: false,
                            isConverting: false,
                            quoteData,
                            count: quoteData.length,
                        };
                        exceptionFiles.push(obj);
                    }

                    let repeatedFiles = [...data.repeateds, ...data.specialStatus];

                    let repeatFiles = [];
                    filesToCheck.forEach(item => {
                        let repeatFind = repeatedFiles.find(el => item.fileName === el.fileName);
                        if (repeatFind) {
                            repeatFiles.push({ fileName: item.fileName, length: item.length, status: repeatFind.status.value });
                        }
                    })
                    let repeatObj = {
                        errMsg: '文件已存在',
                        isModelGroup: false,
                        isExtException: false,
                        isSceneQuoted: false,
                        isOverWrite: true,
                        isConverting: false,
                        files: repeatFiles,
                        count: repeatFiles.length,
                    };
                    if (repeatFiles.length) {
                        exceptionFiles.push(repeatObj);
                    }
                    resolve({ exceptionFiles });
                }
            })
        })
    }

    osgbUploadFileCheck(filesToCheck) {
        return new Promise(resolve => {
            let exceptionFiles = [];
            let params = {
                projId: this.currProjId,
                parentId: this.currDataSetId,
                isUpdate: this.status === 'update' ? true : false,
                fileNames: filesToCheck.map(el => el.fileName)
            }
            createOsgbInFolderCheckService(params).then(response => {
                if (response.isSuccess) {
                    let data = response.data;
                    if (!data.checkResult) {
                        let files = [];
                        filesToCheck.forEach(item => {
                            let find = data.errorFileNames.find(el => item.fileName === el);
                            if (find) {
                                files.push({ fileName: item.fileName, length: item.length });
                            }
                        })
                        let obj = {
                            errMsg: data.message,
                            isModelGroup: false,
                            isExtException: true,
                            isSceneQuoted: false,
                            isOverWrite: false,
                            isConverting: false,
                            files,
                            count: files.length,
                        };
                        exceptionFiles.push(obj);
                    }
                    if (data.dataSetIds.length) {
                        let quoteData = data.dataSetIds;
                        let obj = {
                            errMsg: '文件被场景引用',
                            isModelGroup: false,
                            isExtException: false,
                            isSceneQuoted: true,
                            isOverWrite: false,
                            isConverting: false,
                            quoteData,
                            count: quoteData.length,
                        };
                        exceptionFiles.push(obj);
                    }

                    let repeatedFiles = [...data.repeateds, ...data.specialStatus];

                    let repeatFiles = [];
                    filesToCheck.forEach(item => {
                        let repeatFind = repeatedFiles.find(el => item.fileName === el.fileName);
                        if (repeatFind) {
                            repeatFiles.push({ fileName: item.fileName, length: item.length, status: repeatFind.status.value });
                        }
                    })
                    let repeatObj = {
                        errMsg: '文件已存在',
                        isModelGroup: false,
                        isExtException: false,
                        isSceneQuoted: false,
                        isOverWrite: true,
                        isConverting: false,
                        files: repeatFiles,
                        count: repeatFiles.length,
                    };
                    if (repeatFiles.length) {
                        exceptionFiles.push(repeatObj);
                    }
                    resolve({ exceptionFiles });
                }
            })
        })
    }

    panoramaUploadFileCheck(filesToCheck) {
        return new Promise(resolve => {
            let exceptionFiles = [];
            if (this.status === 'update') {
                resolve({ exceptionFiles });
                return
            }
            let params = {
                projId: this.currProjId,
                parentId: this.currDataSetId,
                fileName: filesToCheck.map(el => el.fileName)
            }
            checkPanoramaFileNameRepeats(params).then(response => {
                if (response.isSuccess) {
                    let data = response.data;
                    let repeatedFiles = [...data.repeateds, ...data.specialStatus];
                    getConvertingQueue({}).then(res => {
                        if (res.isSuccess) {
                            let result = res.data;
                            let convertingFiles = [];
                            if (result.length) {
                                let projQueuing = result;
                                let nodeIds = projQueuing.map(el => el.dataSetId);
                                convertingFiles = repeatedFiles.filter(el => nodeIds.includes(el.dataSetId));
                                repeatedFiles = repeatedFiles.filter(el => !nodeIds.includes(el.dataSetId));
                            }

                            let repeatFiles = [];
                            let isConvertingFiles = [];
                            filesToCheck.forEach(item => {
                                let repeatFind = repeatedFiles.find(el => item.fileName === el.fileName);
                                if (repeatFind) {
                                    repeatFiles.push({ fileName: item.fileName, length: item.length, status: repeatFind.status.value });
                                }

                                let convertingFind = convertingFiles.find(el => item.fileName === el.fileName);
                                if (convertingFind) {
                                    isConvertingFiles.push({ fileName: item.fileName, length: item.length, status: convertingFind.status.value });
                                }
                            })
                            let repeatObj = {
                                errMsg: '文件已存在',
                                isModelGroup: false,
                                isExtException: false,
                                isSceneQuoted: false,
                                isOverWrite: true,
                                isConverting: false,
                                files: repeatFiles,
                                count: repeatFiles.length,
                            };
                            if (repeatFiles.length) {
                                exceptionFiles.push(repeatObj);
                            }

                            let convertingObj = {
                                errMsg: '文件正在转换',
                                isModelGroup: false,
                                isExtException: false,
                                isSceneQuoted: false,
                                isOverWrite: false,
                                isConverting: true,
                                files: isConvertingFiles,
                                count: isConvertingFiles.length,
                            };
                            if (isConvertingFiles.length) {
                                exceptionFiles.push(convertingObj);
                            }
                            resolve({ exceptionFiles });
                        }
                    })


                }
            })
        })
    }

    pointClounUploadFileCheck(filesToCheck) {
        return new Promise(resolve => {
            let exceptionFiles = [];
            let params = {
                projId: this.currProjId,
                parentId: this.currDataSetId,
                isUpdate: this.status === 'update' ? true : false,
                fileNames: filesToCheck.map(el => el.fileName)
            }
            createPointCloudInFolderCheckService(params).then(response => {
                if (response.isSuccess) {
                    let data = response.data;
                    if (!data.checkResult) {
                        let files = [];
                        filesToCheck.forEach(item => {
                            let find = data.errorFileNames.find(el => item.fileName === el);
                            if (find) {
                                files.push({ fileName: item.fileName, length: item.length });
                            }
                        })
                        let obj = {
                            errMsg: data.message,
                            isModelGroup: false,
                            isExtException: true,
                            isSceneQuoted: false,
                            isOverWrite: false,
                            isConverting: false,
                            files,
                            count: files.length,
                        };
                        exceptionFiles.push(obj);
                    }
                    if (data.dataSetIds.length) {
                        let quoteData = data.dataSetIds;
                        let obj = {
                            errMsg: '文件被场景引用',
                            isModelGroup: false,
                            isExtException: false,
                            isSceneQuoted: true,
                            isOverWrite: false,
                            isConverting: false,
                            quoteData,
                            count: quoteData.length,
                        };
                        exceptionFiles.push(obj);
                    }

                    let repeatedFiles = [...data.repeateds, ...data.specialStatus];
                    getConvertingQueue({}).then(res => {
                        if (res.isSuccess) {
                            let result = res.data;
                            let convertingFiles = [];
                            if (result.length) {
                                let projQueuing = result;
                                let nodeIds = projQueuing.map(el => el.dataSetId);
                                convertingFiles = repeatedFiles.filter(el => nodeIds.includes(el.dataSetId));
                                repeatedFiles = repeatedFiles.filter(el => !nodeIds.includes(el.dataSetId));
                            }

                            let repeatFiles = [];
                            let isConvertingFiles = [];
                            filesToCheck.forEach(item => {
                                let repeatFind = repeatedFiles.find(el => item.fileName === el.fileName);
                                if (repeatFind) {
                                    repeatFiles.push({ fileName: item.fileName, length: item.length, status: repeatFind.status.value });
                                }

                                let convertingFind = convertingFiles.find(el => item.fileName === el.fileName);
                                if (convertingFind) {
                                    isConvertingFiles.push({ fileName: item.fileName, length: item.length, status: convertingFind.status.value });
                                }
                            })
                            let repeatObj = {
                                errMsg: '文件已存在',
                                isModelGroup: false,
                                isExtException: false,
                                isSceneQuoted: false,
                                isOverWrite: true,
                                isConverting: false,
                                files: repeatFiles,
                                count: repeatFiles.length,
                            };
                            if (repeatFiles.length) {
                                exceptionFiles.push(repeatObj);
                            }

                            let convertingObj = {
                                errMsg: '文件正在转换',
                                isModelGroup: false,
                                isExtException: false,
                                isSceneQuoted: false,
                                isOverWrite: false,
                                isConverting: true,
                                files: isConvertingFiles,
                                count: isConvertingFiles.length,
                            };
                            if (isConvertingFiles.length) {
                                exceptionFiles.push(convertingObj);
                            }
                            resolve({ exceptionFiles });
                        }
                    })
                }
            })
        })
    }

    cadUploadFileCheck(filesToCheck) {
        return new Promise(resolve => {
            let exceptionFiles = [];
            let params = {
                projId: this.currProjId,
                parentId: this.currDataSetId,
                isUpdate: this.status === 'update' ? true : false,
                fileNames: filesToCheck.map(el => el.fileName)
            }
            createCadInFolderCheckService(params).then(response => {
                if (response.isSuccess) {
                    let data = response.data;
                    if (!data.checkResult) {
                        let files = [];
                        filesToCheck.forEach(item => {
                            let find = data.errorFileNames.find(el => item.fileName === el);
                            if (find) {
                                files.push({ fileName: item.fileName, length: item.length });
                            }
                        })
                        let obj = {
                            errMsg: data.message,
                            isModelGroup: false,
                            isExtException: true,
                            isSceneQuoted: false,
                            isOverWrite: false,
                            isConverting: false,
                            files,
                            count: files.length,
                        };
                        exceptionFiles.push(obj);
                    }
                    if (data.dataSetIds.length) {
                        let quoteData = data.dataSetIds;
                        let obj = {
                            errMsg: '文件被场景引用',
                            isModelGroup: false,
                            isExtException: false,
                            isSceneQuoted: true,
                            isOverWrite: false,
                            isConverting: false,
                            quoteData,
                            count: quoteData.length,
                        };
                        exceptionFiles.push(obj);
                    }

                    let repeatedFiles = [...data.repeateds, ...data.specialStatus];

                    let repeatFiles = [];
                    filesToCheck.forEach(item => {
                        let repeatFind = repeatedFiles.find(el => item.fileName === el.fileName);
                        if (repeatFind) {
                            repeatFiles.push({ fileName: item.fileName, length: item.length, status: repeatFind.status.value });
                        }
                    })
                    let repeatObj = {
                        errMsg: '文件已存在',
                        isModelGroup: false,
                        isExtException: false,
                        isSceneQuoted: false,
                        isOverWrite: true,
                        isConverting: false,
                        files: repeatFiles,
                        count: repeatFiles.length,
                    };
                    if (repeatFiles.length) {
                        exceptionFiles.push(repeatObj);
                    }
                    resolve({ exceptionFiles });
                }
            })
        })
    }

    vectorUploadFileCheck(filesToCheck) {
        return new Promise(resolve => {
            let exceptionFiles = [];
            let params = {
                projId: this.currProjId,
                parentId: this.currDataSetId,
                isUpdate: this.status === 'update' ? true : false,
                fileNames: filesToCheck.map(el => el.fileName)
            }
            createVectorInFolderCheckService(params).then(response => {
                if (response.isSuccess) {
                    let data = response.data;
                    if (!data.checkResult) {
                        let files = [];
                        filesToCheck.forEach(item => {
                            let find = data.errorFileNames.find(el => item.fileName === el);
                            if (find) {
                                files.push({ fileName: item.fileName, length: item.length });
                            }
                        })
                        let obj = {
                            errMsg: data.message,
                            isModelGroup: false,
                            isExtException: true,
                            isSceneQuoted: false,
                            isOverWrite: false,
                            isConverting: false,
                            files,
                            count: files.length,
                        };
                        exceptionFiles.push(obj);
                    }
                    if (data.dataSetIds.length) {
                        let quoteData = data.dataSetIds;
                        let obj = {
                            errMsg: '文件被场景引用',
                            isModelGroup: false,
                            isExtException: false,
                            isSceneQuoted: true,
                            isOverWrite: false,
                            isConverting: false,
                            quoteData,
                            count: quoteData.length,
                        };
                        exceptionFiles.push(obj);
                    }

                    let repeatedFiles = [...data.repeateds, ...data.specialStatus];
                    getConvertingQueue({}).then(res => {
                        if (res.isSuccess) {
                            let result = res.data;
                            let convertingFiles = [];
                            if (result.length) {
                                let projQueuing = result;
                                let nodeIds = projQueuing.map(el => el.dataSetId);
                                convertingFiles = repeatedFiles.filter(el => nodeIds.includes(el.dataSetId));
                                repeatedFiles = repeatedFiles.filter(el => !nodeIds.includes(el.dataSetId));
                            }

                            let repeatFiles = [];
                            let isConvertingFiles = [];
                            filesToCheck.forEach(item => {
                                let repeatFind = repeatedFiles.find(el => item.fileName === el.fileName);
                                if (repeatFind) {
                                    repeatFiles.push({ fileName: item.fileName, length: item.length, status: repeatFind.status.value });
                                }

                                let convertingFind = convertingFiles.find(el => item.fileName === el.fileName);
                                if (convertingFind) {
                                    isConvertingFiles.push({ fileName: item.fileName, length: item.length, status: convertingFind.status.value });
                                }
                            })
                            let repeatObj = {
                                errMsg: '文件已存在',
                                isModelGroup: false,
                                isExtException: false,
                                isSceneQuoted: false,
                                isOverWrite: true,
                                isConverting: false,
                                files: repeatFiles,
                                count: repeatFiles.length,
                            };
                            if (repeatFiles.length) {
                                exceptionFiles.push(repeatObj);
                            }

                            let convertingObj = {
                                errMsg: '文件正在转换',
                                isModelGroup: false,
                                isExtException: false,
                                isSceneQuoted: false,
                                isOverWrite: false,
                                isConverting: true,
                                files: isConvertingFiles,
                                count: isConvertingFiles.length,
                            };
                            if (isConvertingFiles.length) {
                                exceptionFiles.push(convertingObj);
                            }
                            resolve({ exceptionFiles });
                        }
                    })
                }
            })
        })
    }

    terminateUpload() {
        this.uploadFiles.forEach(item => {
            this.uploader.removeFile(item.file, true);
        })
        this.uploadFiles = [];
        this.exceptionFiles = [];
    }

    /**
     * 处理超出文件同时上传个数的情况
     * @param {Object} fileInfo 
     */
    handleFileOverflow(fileInfo) {
        Message.error('超出同时上传文件个数！！！');
        let findIndex = this.uploadingFiles.findIndex(el => this.isSameFile(el, fileInfo))
        if (findIndex !== -1) {
            this.uploadingFiles.splice(findIndex, 1)
            this.uploader.removeFile(fileInfo.file, true);
        }

        if (this.uploadFiles.length) {
            let uploadingFile = this.uploadFiles.shift();
            this.uploadingFiles.push(uploadingFile);
            this.handleUploadSuccess(uploadingFile);
        }
    }

    /**
     * 处理创建文件索引失败
     * @param {Object} fileInfo 
     */
    handleFileCreateFail(fileInfo) {
        let findIndex = this.uploadingFiles.findIndex(el => this.isSameFile(el, fileInfo))
        if (findIndex !== -1) {
            this.uploadingFiles.splice(findIndex, 1)
            this.uploader.removeFile(fileInfo.file, true);
        }

        if (this.uploadFiles.length) {
            let uploadingFile = this.uploadFiles.shift();
            this.uploadingFiles.push(uploadingFile);
            this.handleUploadSuccess(uploadingFile);
        }
    }

    /**
     * 处理文件暂停成功
     * @param {Object} pauseInfo 
     */
    handleFilePauseSuccess(pauseInfo) {
        Message.success("文件暂停成功");
        delete this.uploader.options.formData.pauseFlag;
        delete this.uploader.options.formData.pauseInfo;
        let findIndex = this.uploadingFiles.findIndex((el) => this.isSameFile(el, pauseInfo) && el.dataSetId === pauseInfo.dataSetId);
        if (findIndex !== -1) {
            let fileInfo = this.uploadingFiles[findIndex];
            this.uploadingFiles.splice(findIndex, 1);
            this.uploader.removeFile(fileInfo.file, true);

            fileInfo.customStatus = 'paused';
            fileInfo.status = 2;
            this.pausedFiles.push(fileInfo);
            if (this.uploadFiles.length) {
                let uploadingFile = this.uploadFiles.shift();
                this.uploadingFiles.push(uploadingFile);
                this.handleUploadSuccess(uploadingFile);
            }
        }
    }

    downloadFiles(eventInfo) {
        Message({
            message: '开始下载文件，请勿重复操作......',
            type: 'info',
            duration: 3 * 1000
        })

        let fileData = eventInfo.fileData;
        fileData.forEach((item, index) => {
            let url = "";
            let tokenId = getTokenId();
            if (window.downloadUrl.includes("http")) {
                url = `${window.downloadUrl}/${item.fileDataId}?token=${tokenId}`;
            } else {
                url = `${window.serverUrl}/${window.downloadUrl}/${item.fileDataId}?token=${tokenId}`;
            }

            this.downloadURL(url, index + 1);
        });
    }

    downloadURL(url, id) {
        let hiddenIFrameID = 'hiddenDownloader' + id;
        let iframe = document.createElement('iframe');
        iframe.id = hiddenIFrameID;
        iframe.style.display = 'none';
        document.body.appendChild(iframe);
        iframe.src = url;
    }

    /**
     * 获取已上传文件信息，用于分片上传判断上传到哪一片
     * @param {Object} fileInfo 
     */
    getFileInfo(fileInfo) {
        return new Promise(resolve => {
            getFileInfoService(fileInfo.fileDataId).then((response) => {
                if (response.isSuccess) {
                    let data = response.data;

                    this.uploader.options.formData['FileId'] = fileInfo.fileId;
                    this.uploader.options.formData["FileDataId"] = fileInfo.fileDataId;
                    this.uploader.options.formData["Key"] = data.key;

                    fileInfo["blobIndex"] = data.blobIndex;
                    resolve();
                }
            })
        })
    }

    removeConvertingFilesFromUploadFiles(convertingFiles) {
        convertingFiles.forEach(fileInfo => {
            let findConvertingFileIndex = this.uploadFiles.findIndex(el => el.fileName === fileInfo.fileName);
            if (findConvertingFileIndex !== -1) {
                this.uploadFiles.splice(findConvertingFileIndex, 1);
            }
        })
    }

    removeUploadingFilesFromUploadFiles(repeatedFiles) {
        let uploadingFiles = repeatedFiles.filter(el => UploadingStatusValue.includes(el.status));
        uploadingFiles.forEach(fileInfo => {
            let findIndex = this.uploadFiles.findIndex(el => el.fileName === fileInfo.fileName)
            if (findIndex !== -1) {
                this.uploadFiles.splice(findIndex, 1);
            }

            let findIndexFromRepeatedFiles = repeatedFiles.findIndex(el => el.fileId === fileInfo.fileId);
            if (findIndexFromRepeatedFiles !== -1) {
                repeatedFiles.splice(findIndexFromRepeatedFiles, 1)
            }
        })
    }

    isFileInUploadQueue(fileInfo) {
        let fileInUploadFiles = this.uploadFiles.find(el => this.isSameFile(el, fileInfo) && el.dataSetId === fileInfo.currDataSetId);
        let fileInUploadingFiles = this.uploadingFiles.find(el => this.isSameFile(el, fileInfo) && el.dataSetId === fileInfo.currDataSetId);
        return fileInUploadFiles || fileInUploadingFiles;
    }

    isInvalidFileName(fileInfo) {
        let fileName = fileInfo.fileName.substring(0, fileInfo.fileName.lastIndexOf("."));
        if (fileName.length !== fileName.trimStart().trimEnd().length || fileName.includes('\\')) {
            return true;
        } else {
            return false;
        }
    }

    isVectorInvalidFileName(fileInfo) {
        if (this.schemeType !== 'Vector') return false;
        let fileName = fileInfo.fileName
        let fileType = fileName.substring(
            fileName.lastIndexOf(".") + 1
        );
        if (fileType === 'zip') {
            if (fileName.includes('shp.zip')) {
                return false
            } else {
                return true
            }
        } else {
            return false;
        }
    }

    /**
     * 文件是否经过同名校验(断点续传和重新上传的文件不需要重名校验)
     * @returns {Boolean} 是否校验过
     */
    isChecked() {
        if (this.status === 'waiting' || this.status === 'update') {
            return false;
        } else {
            return true;
        }
    }

    getFileStatus() {
        let status = null;
        switch (this.status) {
            case 'waiting':
                status = -1
                break;
            case 'continue':
                status = -2
                break;
            case 'update':
                status = -3
                break;

        }
        return status;
    }

    isSameFile(fileInfo1, fileInfo2) {
        return fileInfo1.fileName === fileInfo2.fileName && fileInfo1.length === fileInfo2.length
    }

    setStatus(status) {
        this.status = status;
    }

    setCurrProjId(currProjId) {
        this.currProjId = currProjId;
    }

    setCurrNodeType(currNodeType) {
        this.currNodeType = currNodeType;
    }

    setCurrDataSetId(currDataSetId) {
        this.currDataSetId = currDataSetId;
    }

    setUploadPathName(uploadPathName) {
        this.uploadPathName = uploadPathName;
    }

    setCurrFileInfo(fileInfo) {
        this.currFileInfo = fileInfo;
    }

    getWarningMessage(fileNameList, messageTail) {
        let message = "";
        let messageHead = "";
        // 超过两个文件只展示前两个
        if (fileNameList.length > 2) {
            messageHead = `${fileNameList[0]}、${fileNameList[1]}等${fileNameList.length}个文件`;
        } else {
            messageHead = fileNameList.join('、');
        }
        message = `${messageHead}${messageTail}`
        return message;
    }

    setAppendUrl(appendType) {
        this.uploader.options.server = `${window.serverUrl}/${logServeCof.logServeCof()}/project/${AppendInterfaceMap[this.schemeType]}/v3/${appendType}`;
    }
}

export default CustomUploader;